// activity-fields (fourth axis, poc-contract.md). Shared constants of the
// content / limitation fields so the schema, the payload builder and the
// editors cannot drift from the property-service validator caps.

export const TEXT_BLOCK_MAX = 5000
export const LIST_MAX_ITEMS = 10
export const LIST_ITEM_MAX = 120
export const PMS_CODE_MAX = 64

export const LIST_FIELDS = ['highlights', 'inclusions', 'exclusions'] as const
export type ListField = (typeof LIST_FIELDS)[number]

export const TEXT_BLOCK_FIELDS = [
  'whatToBring',
  'requirements',
  'postBookingInfo',
] as const

// Keys a tier inherits from its base at read time (F-3). property-service
// answers 400 when a variation write carries any of them, so the tier payload
// drops them. activityCode is NOT here: each tier owns its code (F-2).
export const INHERITED_DETAIL_KEYS = [
  'location',
  'locationUrl',
  'highlights',
  'inclusions',
  'exclusions',
  'whatToBring',
  'requirements',
  'postBookingInfo',
  'durationMinutes',
  'languages',
  'minQuantity',
  'minAge',
  'maxAge',
] as const

// The three Limitations-tab keys live under `limitations` in the form (so a
// validation error jumps to the right tab) but travel in `activityDetails`.
export const LIMITATION_DETAIL_KEYS = ['minQuantity', 'minAge', 'maxAge'] as const

// ISO 639-1 two-letter codes: the server only accepts these. Labels come from
// Intl.DisplayNames so the list follows the admin locale without i18n keys.
export const ISO_639_1_CODES = [
  'aa', 'ab', 'ae', 'af', 'ak', 'am', 'an', 'ar', 'as', 'av', 'ay', 'az',
  'ba', 'be', 'bg', 'bi', 'bm', 'bn', 'bo', 'br', 'bs', 'ca', 'ce', 'ch',
  'co', 'cr', 'cs', 'cu', 'cv', 'cy', 'da', 'de', 'dv', 'dz', 'ee', 'el',
  'en', 'eo', 'es', 'et', 'eu', 'fa', 'ff', 'fi', 'fj', 'fo', 'fr', 'fy',
  'ga', 'gd', 'gl', 'gn', 'gu', 'gv', 'ha', 'he', 'hi', 'ho', 'hr', 'ht',
  'hu', 'hy', 'hz', 'ia', 'id', 'ie', 'ig', 'ii', 'ik', 'io', 'is', 'it',
  'iu', 'ja', 'jv', 'ka', 'kg', 'ki', 'kj', 'kk', 'kl', 'km', 'kn', 'ko',
  'kr', 'ks', 'ku', 'kv', 'kw', 'ky', 'la', 'lb', 'lg', 'li', 'ln', 'lo',
  'lt', 'lu', 'lv', 'mg', 'mh', 'mi', 'mk', 'ml', 'mn', 'mr', 'ms', 'mt',
  'my', 'na', 'nb', 'nd', 'ne', 'ng', 'nl', 'nn', 'no', 'nr', 'nv', 'ny',
  'oc', 'oj', 'om', 'or', 'os', 'pa', 'pi', 'pl', 'ps', 'pt', 'qu', 'rm',
  'rn', 'ro', 'ru', 'rw', 'sa', 'sc', 'sd', 'se', 'sg', 'si', 'sk', 'sl',
  'sm', 'sn', 'so', 'sq', 'sr', 'ss', 'st', 'su', 'sv', 'sw', 'ta', 'te',
  'tg', 'th', 'ti', 'tk', 'tl', 'tn', 'to', 'tr', 'ts', 'tt', 'tw', 'ty',
  'ug', 'uk', 'ur', 'uz', 've', 'vi', 'vo', 'wa', 'wo', 'xh', 'yi', 'yo',
  'za', 'zh', 'zu',
] as const

const ISO_SET = new Set<string>(ISO_639_1_CODES)
export const isIso6391 = (code: string) => ISO_SET.has(code)

export const languageOptions = (locale: string) => {
  let names: Intl.DisplayNames | null = null
  try {
    names = new Intl.DisplayNames([locale], { type: 'language' })
  } catch {
    names = null
  }
  return ISO_639_1_CODES.map((code) => ({
    value: code,
    label: names?.of(code) ?? code,
  })).sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Minutes of a slot string "HH:mm-HH:mm". Used only to pre-fill
 * durationMinutes, which stays informational (the slot string remains the
 * single source of truth for timing). Slots crossing midnight wrap; a
 * start-only slot (D-10) has no length, so nothing is pre-filled.
 */
export const durationFromSlot = (time: string | undefined): number | null => {
  const match = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/.exec(time ?? '')
  if (!match) return null
  const [, h1, m1, h2, m2] = match.map(Number)
  const minutes = h2 * 60 + m2 - (h1 * 60 + m1)
  const wrapped = minutes <= 0 ? minutes + 24 * 60 : minutes
  return wrapped > 0 ? wrapped : null
}
